import type {
	DiscriminatorSpec,
	DiscriminatorSpecFields,
	TaxonomyClass,
	TaxonomyDraft,
	TaxonomyDraftIssue,
} from "@sitescene/types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { useOrgSelection } from "@/context/use-org-selection";

/** Wire shape of `GET /shadow/plausibility/sensor-specs/workspace`. */
export interface SensorSpecWorkspace {
	classes: TaxonomyClass[];
	draft: null | TaxonomyDraft;
	globalSpecs: { classId: string; fields: DiscriminatorSpecFields }[];
	issues: TaxonomyDraftIssue[];
	overrides: DiscriminatorSpec[];
	sensors: { id: string; installation: string; name: string }[];
}

/** The selected organization's cameras, overrides, and override draft. */
export const useSensorSpecWorkspace = () => {
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? null;
	return useQuery({
		enabled: orgCode !== null,
		queryFn: async () =>
			(
				await apiClient.get<SensorSpecWorkspace>(
					ENDPOINTS.shadow.plausibility.sensorSpecsWorkspace,
				)
			).data,
		queryKey: queryKeys.plausibility.sensorSpecs(orgCode ?? ""),
	});
};

export const useSensorSpecEditor = () => {
	const queryClient = useQueryClient();
	const { selected } = useOrgSelection();
	const refresh = () =>
		queryClient.invalidateQueries({
			queryKey: queryKeys.plausibility.sensorSpecs(selected?.orgCode ?? ""),
		});

	const saveSpec = useMutation({
		mutationFn: async (input: {
			classId: string;
			fields: DiscriminatorSpecFields;
			sensorId: string;
		}) =>
			(
				await apiClient.put<TaxonomyDraft>(
					ENDPOINTS.shadow.plausibility.sensorSpec(
						input.sensorId,
						input.classId,
					),
					input.fields,
				)
			).data,
		onSuccess: refresh,
	});

	const revertSpec = useMutation({
		mutationFn: async (input: { classId: string; sensorId: string }) =>
			(
				await apiClient.delete<TaxonomyDraft>(
					ENDPOINTS.shadow.plausibility.sensorSpec(
						input.sensorId,
						input.classId,
					),
				)
			).data,
		onSuccess: refresh,
	});

	const publish = useMutation({
		mutationFn: async () =>
			(
				await apiClient.post<{ specs: number; version: number }>(
					ENDPOINTS.shadow.plausibility.sensorSpecsPublish,
				)
			).data,
		onSettled: refresh,
	});

	const discard = useMutation({
		mutationFn: async () =>
			(await apiClient.delete(ENDPOINTS.shadow.plausibility.sensorSpecsDraft))
				.data,
		onSuccess: refresh,
	});

	return { discard, publish, revertSpec, saveSpec };
};
